/**
 * Server-Sent Events client for the Stella streaming chat handler.
 *
 * `EventSource` is the obvious browser API for SSE — but it does not
 * support custom request headers, so we cannot send the
 * `Authorization: Bearer …` token with it. We use fetch +
 * ReadableStream.getReader() and parse SSE frames by hand.
 *
 * Frame shape (one event per Bedrock content delta):
 *
 *   event: token
 *   data: {"text": "<chunk>"}\n\n
 *
 * The terminal frame is `event: done`. Errors emit `event: error`.
 */

export interface StreamChatArgs {
  url: string;
  idToken: string;
  body: unknown;
  onToken: (text: string) => void;
  onDone: (info: {
    inputTokens: number;
    outputTokens: number;
    stopReason: string;
    truncated: boolean;
  }) => void;
  onError: (err: { code: string; message: string }) => void;
  // Spec 004 — visual picker + structured estimate.
  onOptions?: (payload: unknown) => void;
  onEstimate?: (payload: unknown) => void;
  // Spec 007 — design sketch.
  onDesign?: (payload: unknown) => void;
  // Spec 008 — photos picker.
  onPhotos?: (payload: unknown) => void;
  // Spec 014 — inline measure directive. Fires when Stella's reply
  // contains a `[[MEASURE:mode=…]]` directive; payload shape is
  // `{ mode: "linear" | "surface" | "room", label: string | null }`.
  onMeasure?: (payload: unknown) => void;
  // Phase 18 — add-photo directive. Fires when Stella's reply
  // contains `[[ADD_PHOTO]]`; payload is `{ photo_request: true }`.
  onAddPhoto?: (payload: unknown) => void;
  // Phase 18 — render directive. Fires when Stella's reply contains
  // `[[RENDER:photo_id=…;prompt=…]]`; payload is the RenderPayload
  // shape so the client can render the spinner card immediately.
  onRender?: (payload: unknown) => void;
  // Phase 18.7 — offer-render directive. Fires when Stella's reply
  // contains `[[OFFER_RENDER]]`; payload is `{ offer_render: true }`.
  // The client renders an inline "Render the design on my photo"
  // button below the message; tapping it sends a synthetic user turn.
  onOfferRender?: (payload: unknown) => void;
  // Phase 19.1 — contact-Starz directive. Fires when Stella's reply
  // contains `[[CONTACT_STARZ]]` (or the pricing pipeline returned
  // RequiresConsultation). Payload is `{ contact_starz: true,
  // payload: ContactStarzPayload }`. Client renders an inline
  // Email Starz / Call Starz button row.
  onContactStarz?: (payload: unknown) => void;
  signal?: AbortSignal;
}

export async function streamChat(args: StreamChatArgs): Promise<void> {
  const {
    url,
    idToken,
    body,
    onToken,
    onDone,
    onError,
    onOptions,
    onEstimate,
    onDesign,
    onPhotos,
    onMeasure,
    onAddPhoto,
    onRender,
    onOfferRender,
    onContactStarz,
    signal,
  } = args;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) return;
    onError({
      code: "NETWORK",
      message: err instanceof Error ? err.message : "Network error",
    });
    return;
  }

  // RN's lib.d.ts types `Response` without `body` (the streaming
  // ReadableStream); web's has it. We polyfill at runtime on RN
  // (web-streams-polyfill + react-native-fetch-api when used directly
  // for chat). Cast through `unknown` to accept both shapes.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const responseAny = response as any;
  if (!response.ok) {
    // The server sent a non-200 status; try to surface a parsed
    // error frame from the body if present, otherwise generic.
    const text = await safeReadText(response);
    const parsed = tryParseError(text);
    onError(
      parsed ?? {
        code: response.status === 401 ? "UNAUTHENTICATED" : "INTERNAL",
        message: `Chat request failed (${response.status})`,
      },
    );
    return;
  }

  // RN fallback: native fetch doesn't expose a streaming ReadableStream
  // on Response. Buffer the whole text response and dispatch frames in
  // one shot — chat appears all-at-once instead of progressively, but
  // it works without breaking Amplify's auth flow (Phase 10 tradeoff).
  if (!responseAny.body) {
    const fullText = await safeReadText(response);
    dispatchFrames(fullText, {
      onToken,
      onDone: (info) => {
        onDone(info);
      },
      onError,
      onOptions,
      onEstimate,
      onDesign,
      onPhotos,
      onMeasure,
      onAddPhoto,
      onRender,
      onOfferRender,
      onContactStarz,
    });
    return;
  }

  const reader = responseAny.body.getReader();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const decoder = new (globalThis as any).TextDecoder();
  let buffer = "";
  let sawDone = false;

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line (\n\n).
      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        if (!frame.trim()) continue;
        const parsed = parseFrame(frame);
        if (!parsed) continue;
        const { event, data } = parsed;

        if (event === "token") {
          const text = (data as { text?: string }).text ?? "";
          if (text) onToken(text);
        } else if (event === "done") {
          sawDone = true;
          onDone({
            inputTokens: Number((data as { input_tokens?: number }).input_tokens ?? 0),
            outputTokens: Number((data as { output_tokens?: number }).output_tokens ?? 0),
            stopReason: String((data as { stop_reason?: string }).stop_reason ?? "end_turn"),
            truncated: Boolean((data as { truncated?: boolean }).truncated ?? false),
          });
        } else if (event === "error") {
          onError({
            code: String((data as { code?: string }).code ?? "INTERNAL"),
            message: String((data as { message?: string }).message ?? "Unknown error"),
          });
        } else if (event === "options" && onOptions) {
          onOptions(data);
        } else if (event === "estimate" && onEstimate) {
          onEstimate(data);
        } else if (event === "design" && onDesign) {
          onDesign(data);
        } else if (event === "photos" && onPhotos) {
          onPhotos(data);
        } else if (event === "measure" && onMeasure) {
          onMeasure(data);
        } else if (event === "add_photo" && onAddPhoto) {
          onAddPhoto(data);
        } else if (event === "render" && onRender) {
          onRender(data);
        } else if (event === "offer_render" && onOfferRender) {
          onOfferRender(data);
        } else if (event === "contact_starz" && onContactStarz) {
          onContactStarz(data);
        }
      }
    }
  } catch (err) {
    if (signal?.aborted) return;
    onError({
      code: "NETWORK",
      message: err instanceof Error ? err.message : "Stream interrupted",
    });
  }

  if (!sawDone) {
    // Connection ended without a `done` frame (server killed by
    // wall-clock timeout, or browser tab closed mid-stream). Surface
    // a synthetic done so the UI can settle.
    onDone({
      inputTokens: 0,
      outputTokens: 0,
      stopReason: "interrupted",
      truncated: true,
    });
  }
}

interface ParsedFrame {
  event: string;
  data: unknown;
}

function parseFrame(frame: string): ParsedFrame | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith(":")) continue; // comment
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    const value = line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  const dataString = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(dataString) };
  } catch {
    return { event, data: dataString };
  }
}

interface FrameHandlers {
  onToken: (text: string) => void;
  onDone: (info: {
    inputTokens: number;
    outputTokens: number;
    stopReason: string;
    truncated: boolean;
  }) => void;
  onError: (err: { code: string; message: string }) => void;
  onOptions?: (payload: unknown) => void;
  onEstimate?: (payload: unknown) => void;
  onDesign?: (payload: unknown) => void;
  onPhotos?: (payload: unknown) => void;
  onMeasure?: (payload: unknown) => void;
  onAddPhoto?: (payload: unknown) => void;
  onRender?: (payload: unknown) => void;
  onOfferRender?: (payload: unknown) => void;
  onContactStarz?: (payload: unknown) => void;
}

function dispatchFrames(buffer: string, handlers: FrameHandlers): void {
  const {
    onToken, onDone, onError, onOptions, onEstimate, onDesign, onPhotos, onMeasure, onAddPhoto, onRender, onOfferRender, onContactStarz,
  } = handlers;
  let sawDone = false;
  for (const rawFrame of buffer.split("\n\n")) {
    if (!rawFrame.trim()) continue;
    const parsed = parseFrame(rawFrame);
    if (!parsed) continue;
    const { event, data } = parsed;
    if (event === "token") {
      const text = (data as { text?: string }).text ?? "";
      if (text) onToken(text);
    } else if (event === "done") {
      sawDone = true;
      onDone({
        inputTokens: Number((data as { input_tokens?: number }).input_tokens ?? 0),
        outputTokens: Number((data as { output_tokens?: number }).output_tokens ?? 0),
        stopReason: String((data as { stop_reason?: string }).stop_reason ?? "end_turn"),
        truncated: Boolean((data as { truncated?: boolean }).truncated ?? false),
      });
    } else if (event === "error") {
      onError({
        code: String((data as { code?: string }).code ?? "INTERNAL"),
        message: String((data as { message?: string }).message ?? "Unknown error"),
      });
    } else if (event === "options" && onOptions) {
      onOptions(data);
    } else if (event === "estimate" && onEstimate) {
      onEstimate(data);
    } else if (event === "design" && onDesign) {
      onDesign(data);
    } else if (event === "photos" && onPhotos) {
      onPhotos(data);
    } else if (event === "measure" && onMeasure) {
      onMeasure(data);
    } else if (event === "add_photo" && onAddPhoto) {
      onAddPhoto(data);
    } else if (event === "render" && onRender) {
      onRender(data);
    } else if (event === "offer_render" && onOfferRender) {
      onOfferRender(data);
    } else if (event === "contact_starz" && onContactStarz) {
      onContactStarz(data);
    }
  }
  if (!sawDone) {
    onDone({ inputTokens: 0, outputTokens: 0, stopReason: "interrupted", truncated: true });
  }
}

async function safeReadText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

function tryParseError(text: string): { code: string; message: string } | null {
  if (!text) return null;
  // Parse a single SSE error frame if that's all we got.
  const parsed = parseFrame(text.replace(/\n\n$/, ""));
  if (parsed && parsed.event === "error" && parsed.data && typeof parsed.data === "object") {
    const data = parsed.data as { code?: string; message?: string };
    return {
      code: String(data.code ?? "INTERNAL"),
      message: String(data.message ?? "Unknown error"),
    };
  }
  // Or a JSON error envelope.
  try {
    const json = JSON.parse(text);
    if (json?.error) {
      return {
        code: String(json.error.code ?? "INTERNAL"),
        message: String(json.error.message ?? "Unknown error"),
      };
    }
  } catch {
    /* no-op */
  }
  return null;
}
